// @ts-ignore
import React from 'react';
// @ts-ignore
import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getLogoUrlSync, getLogoUrl, saveLogoUrl, resetLogoToDefault } from '../../../utils/logoUtils';
import Notification from '../../ui/Notification';
// @ts-ignore
import { useNavigate } from 'react-router-dom';

// API URL configuration
const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
const API_BASE = API_URL === '/api' ? '' : API_URL;

const SettingsContainer = styled.div`
  padding: 2rem;
  max-width: 1200px;
  margin: 0 auto;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, 'Open Sans', 'Helvetica Neue', sans-serif;
`;

const SettingsCard = styled.div`
  background-color: #ffffff;
  border-radius: 16px; /* Slightly more rounded for a softer look */
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.07); /* Slightly enhanced shadow */
  padding: 2rem;
  margin-bottom: 2rem;
  border: 1px solid #e5e7eb; /* Subtle border */
`;

const SectionTitle = styled.h2`
  font-size: 1.5rem; /* Increased size */
  color: #111827; /* Darker, modern text color */
  font-weight: 600;
  margin-top: 0;
  margin-bottom: 1.5rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid #e5e7eb;
`;

const SettingsHeader = styled.h1`
  font-size: 2rem;
  color: #111827;
  font-weight: 700;
  margin-bottom: 1rem;
`;

const SettingsForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem; /* Increased gap */
  margin-bottom: 1.5rem; /* Add bottom margin for spacing between groups */
`;

const Label = styled.label`
  font-weight: 500;
  color: #374151; /* Slightly darker for better contrast */
  font-size: 0.875rem;
`;

const HelperText = styled.p`
  font-size: 0.8rem;
  color: #6b7280;
  margin-top: -0.5rem; /* Position closer to the label or input */
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  padding: 0.8rem 1rem;
  border: 1px solid #d1d5db; /* Softer border color */
  border-radius: 8px; /* More rounded */
  font-size: 0.9rem;
  color: #111827;
  background-color: #f9fafb;
  width: 100%;
  max-width: 450px;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #4361ee;
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
    background-color: #ffffff;
  }

  &::placeholder {
    color: #9ca3af;
  }
`;

const Select = styled.select`
  padding: 0.8rem 1rem;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 0.9rem;
  color: #111827;
  background-color: #f9fafb;
  width: 100%;
  max-width: 450px;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;

  &:focus {
    outline: none;
    border-color: #4361ee;
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
    background-color: #ffffff;
  }
`;

const Checkbox = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  input {
    margin: 0;
  }
`;

const Button = styled.button`
  background-color: #4361ee;
  color: white;
  border: none;
  border-radius: 8px; /* More rounded */
  padding: 0.75rem 1.5rem; /* Adjusted padding for better feel */
  font-size: 0.9rem;
  font-weight: 600; /* Slightly bolder */
  cursor: pointer;
  width: fit-content;
  transition: background-color 0.2s ease-in-out, transform 0.1s ease-in-out;
  display: inline-flex; /* For aligning icon and text */
  align-items: center;
  gap: 0.5rem;

  &:hover {
    background-color: #3a56d4;
    transform: translateY(-1px);
  }
  
  &:active {
    transform: translateY(0px);
  }

  &:disabled {
    background-color: #9ca3af;
    color: #e5e7eb;
    cursor: not-allowed;
    transform: translateY(0px);
  }
`;

const SecondaryButton = styled(Button)`
  background-color: #e5e7eb;
  color: #374151;
  border: 1px solid #d1d5db;

  &:hover {
    background-color: #d1d5db;
    color: #111827;
  }

  &:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    border-color: #e5e7eb;
  }
`;

const ToggleSwitchContainer = styled.label`
  position: relative;
  display: inline-block;
  width: 48px; /* Slightly adjusted */
  height: 26px;
  
  input {
    opacity: 0;
    width: 0;
    height: 0;
  }
`;

const ToggleSlider = styled.span`
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #cbd5e0;
  transition: .3s;
  border-radius: 26px;

  &:before {
    position: absolute;
    content: "";
    height: 20px;
    width: 20px;
    left: 3px;
    bottom: 3px;
    background-color: white;
    transition: .3s;
    border-radius: 50%;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }

  input:checked + & {
    background-color: #4361ee;
  }
  
  input:focus + & {
    box-shadow: 0 0 0 3px rgba(67, 97, 238, 0.15);
  }

  input:checked + &:before {
    transform: translateX(22px);
  }
`;

const ToggleRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  max-width: 400px;
`;

const ToggleLabel = styled.span`
  font-weight: 500;
  color: #374151;
  font-size: 0.9rem;
`;

const SaveButton = styled(Button)`
  margin-top: 1rem;
`;

interface SuccessMessageProps {
  visible: boolean;
}

const SuccessMessage = styled.div<SuccessMessageProps>`
  background-color: #c6f6d5;
  color: #2f855a;
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-top: 1rem;
  display: ${props => props.visible ? 'block' : 'none'};
`;

interface TestResultProps {
  success: boolean;
}

const TestResult = styled.div<TestResultProps>`
  background-color: ${props => props.success ? '#c6f6d5' : '#fed7d7'};
  color: ${props => props.success ? '#2f855a' : '#c53030'};
  padding: 0.75rem 1rem;
  border-radius: 4px;
  margin-top: 1rem;
`;

const TestButton = styled(Button)`
  margin-left: 1rem;
  background-color: #4299e1;
  
  &:hover {
    background-color: #3182ce;
  }
  
  &:disabled {
    background-color: #a0aec0;
  }
`;

const PasswordInput = styled(Input)`
  letter-spacing: 0.1em;
`;

const SectionDivider = styled.hr`
  border: none;
  border-top: 1px solid #e5e7eb; /* Consistent border color */
  margin: 2rem 0;
`;

const TabContainer = styled.div`
  display: flex;
  flex-wrap: wrap; /* Allow tabs to wrap to the next line */
  gap: 0.5rem; /* Add a small gap between tab buttons when they wrap */
  margin-bottom: 2rem;
  padding-bottom: 1px; /* To ensure border doesn't overlap wrapped items if margin-bottom on TabButton is -1px */
  border-bottom: 1px solid #e5e7eb;
`;

const TabButton = styled.button<{ isActive: boolean }>`
  padding: 0.75rem 1rem; /* Adjusted horizontal padding slightly */
  font-size: 0.9rem;
  font-weight: 500;
  color: ${props => props.isActive ? '#4361ee' : '#6b7280'};
  background-color: ${props => props.isActive ? 'rgba(67, 97, 238, 0.05)' : 'transparent'}; /* Subtle bg for active tab */
  border: none;
  border-bottom: 2px solid ${props => props.isActive ? '#4361ee' : 'transparent'};
  border-radius: 6px 6px 0 0; /* Slightly rounded top corners for active tab button */
  cursor: pointer;
  margin-bottom: -1px; /* Align with container's border */
  transition: color 0.2s ease, border-color 0.2s ease, background-color 0.2s ease;

  flex: 1; /* Allow tabs to grow and shrink to fill space in a row */
  min-width: 120px; /* Ensure tabs have a minimum width */
  text-align: center; /* Center the text within the tab */

  &:hover {
    color: #4361ee;
    background-color: rgba(67, 97, 238, 0.03);
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(67, 97, 238, 0.2); /* Focus ring */
  }
`;

const TabContent = styled.div`
  /* SettingsCard will be used within TabContent for structure */
`;

interface SettingsState {
  companyName: string;
  adminEmail: string;
  notificationsEnabled: boolean;
  applicationAutoApproval: boolean;
  maxFileSize: string;
  defaultApplicationStatus: string;
  maintenanceMode: boolean;
  applicationsClosed: boolean;
  applicationDeadline: string;
  emailNotifications: boolean;
  requireOTP: boolean;
  
  // Logo settings
  logoUrl: string;
  
  // TinyPNG API settings
  tinyPngApiKey: string;
  tinyPngCompressImages: boolean;
  
  // Gmail SMTP settings
  smtpHost: string;
  smtpPort: string;
  smtpUsername: string;
  smtpPassword?: string; // Made optional as it might be placeholder
  smtpFromEmail: string;
  smtpFromName: string;
  smtpSecure: boolean;

  // Razorpay Settings
  razorpayKeyId?: string;
  razorpayKeySecretConfigured?: boolean;
  razorpayWebhookSecretConfigured?: boolean;
  
  // Document Request Feature
  documentRequestFeatureEnabled: boolean;
  
  // Session Timeout Settings
  sessionTimeoutMinutes: number;
  
  // Important Settings - Hide Payment Button
  hidePaymentButton: boolean;
  
  // Important Settings - Payment Amount
  paymentAmount: number;
  
  // Mandatory Fields Settings for Page 1
  mandatoryFields: {
    profilePhoto: boolean;
    advertisementSource: boolean;
    cngStationLocation: boolean;
    applicationType: boolean;
    panCardUpload: boolean;
    minimumEducation: boolean;
    currentAddress: boolean;
    personalMobile: boolean;
    nationality: boolean;
    maritalStatus: boolean;
    gender: boolean;
    kycAadhaarPan: boolean;
    educationalQualification: boolean;
    businessEmploymentDetails: boolean;
    financialCapability: boolean;
    financialCapabilityDocChecklist: boolean;
    appendixCards: boolean;
  };
  
  // Mandatory Fields Settings for Page 2
  mandatoryFieldsPage2: {
    // OFFERED LAND/PLOT OWNERSHIP DETAILS (MANDATORY)
    landOwnershipStatus: boolean;
    periodOfLease: boolean;
    landPlotDrawingFile: boolean;
    landCompleteAddress: boolean;
    landLocationCoordinates: boolean;
    landSurveyNo: boolean;
    landKhataNo: boolean;
    landKhatauniNo: boolean;
    landFerfarNo: boolean;
    landVargaNo: boolean;
    mnglLandFrontageMtrs: boolean;
    mnglLandDepthMtrs: boolean;
    mnglTotalAreaSqMtrs: boolean;
    landApproachRoadWidthMtrs: boolean;
    landPlotLevelType: boolean;
    // Surroundings of Offered Land/Plot
    surroundingsEast: boolean;
    surroundingsWest: boolean;
    surroundingsNorth: boolean;
    surroundingsSouth: boolean;
    // OTHER INFORMATION (OFFERED LAND/PLOT RELATED)
    landUse: boolean;
    landTitleClear: boolean;
    landLitigationFree: boolean;
    landPowerConnectionAvailable: boolean;
    landWaterConnectionAvailable: boolean;
    landEncroachment: boolean;
    landGovtAcquisitionPlan: boolean;
    nearestPoliceStationName: boolean;
    nearestFireStationName: boolean;
    highVoltageTransmissionLine: boolean;
    landAbuttingRoadBoundary: boolean;
  };

  // Auto Email Database Backup
  autoEmailBackupEnabled: boolean;
  autoEmailBackupIntervalMinutes: number;
}

interface NotificationState {
  isVisible: boolean;
  type: 'success' | 'error' | 'info' | 'warning';
  message: string;
}

const LogoPreview = styled.div`
  width: 200px;
  height: 80px;
  border: 1px dashed #e2e8f0;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  background-color: #f7fafc;
  overflow: hidden;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
`;

const FileUploadButton = styled.label`
  display: inline-block;
  background-color: #4361ee;
  color: white;
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  margin-right: 1rem;
  transition: background-color 0.3s ease;
  
  &:hover {
    background-color: #3a56d4;
  }
  
  input {
    display: none;
  }
`;

const RemoveButton = styled.button`
  background-color: #e53e3e;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 0.5rem 1rem;
  font-weight: 500;
  cursor: pointer;
  
  &:hover {
    background-color: #c53030;
  }
`;

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'important', label: 'Important Settings' },
  { id: 'mandatory', label: 'Mandatory Fields' },
  { id: 'database', label: 'Database Settings' },
  { id: 'branding', label: 'Branding & Appearance' },
  { id: 'notifications', label: 'Notifications' },
  { id: 'security', label: 'Security & Access' },
  { id: 'integrations', label: 'Integrations' },
  { id: 'smtp', label: 'Email (SMTP)' },
  { id: 'advanced', label: 'Advanced' },
];

const Settings: React.FC = () => {
  const [settings, setSettings] = useState<SettingsState>({
    companyName: '',
    adminEmail: '',
    notificationsEnabled: true,
    applicationAutoApproval: false,
    maxFileSize: '10', // Default to 10MB
    defaultApplicationStatus: 'pending',
    maintenanceMode: false,
    applicationsClosed: false,
    applicationDeadline: '',
    emailNotifications: true,
    requireOTP: false,
    logoUrl: getLogoUrlSync(), // Initialize with current logo
    tinyPngApiKey: '',
    tinyPngCompressImages: false,
    smtpHost: '',
    smtpPort: '',
    smtpUsername: '',
    smtpPassword: '',
    smtpFromEmail: '',
    smtpFromName: '',
    smtpSecure: false,
    // Razorpay Settings
    razorpayKeyId: '',
    razorpayKeySecretConfigured: false,
    razorpayWebhookSecretConfigured: false,
    // Document Request Feature
    documentRequestFeatureEnabled: true,
    // Session Timeout Settings (default 6 hours = 360 minutes)
    sessionTimeoutMinutes: 360,
    // Important Settings - Hide Payment Button
    hidePaymentButton: false,
    // Important Settings - Payment Amount
    paymentAmount: 5000,
    // Mandatory Fields Settings for Page 1 (all default to true = mandatory)
    mandatoryFields: {
      profilePhoto: true,
      advertisementSource: true,
      cngStationLocation: true,
      applicationType: true,
      panCardUpload: true,
      minimumEducation: true,
      currentAddress: true,
      personalMobile: true,
      nationality: true,
      maritalStatus: true,
      gender: true,
      kycAadhaarPan: true,
      educationalQualification: true,
      businessEmploymentDetails: true,
      financialCapability: true,
      financialCapabilityDocChecklist: true,
      appendixCards: true,
    },
    // Mandatory Fields Settings for Page 2 (all default to true = mandatory)
    mandatoryFieldsPage2: {
      // OFFERED LAND/PLOT OWNERSHIP DETAILS (MANDATORY)
      landOwnershipStatus: true,
      periodOfLease: true,
      landPlotDrawingFile: true,
      landCompleteAddress: true,
      landLocationCoordinates: true,
      landSurveyNo: true,
      landKhataNo: true,
      landKhatauniNo: true,
      landFerfarNo: true,
      landVargaNo: true,
      mnglLandFrontageMtrs: true,
      mnglLandDepthMtrs: true,
      mnglTotalAreaSqMtrs: true,
      landApproachRoadWidthMtrs: true,
      landPlotLevelType: true,
      // Surroundings of Offered Land/Plot
      surroundingsEast: true,
      surroundingsWest: true,
      surroundingsNorth: true,
      surroundingsSouth: true,
      // OTHER INFORMATION (OFFERED LAND/PLOT RELATED)
      landUse: true,
      landTitleClear: true,
      landLitigationFree: true,
      landPowerConnectionAvailable: true,
      landWaterConnectionAvailable: true,
      landEncroachment: true,
      landGovtAcquisitionPlan: true,
      nearestPoliceStationName: true,
      nearestFireStationName: true,
      highVoltageTransmissionLine: true,
      landAbuttingRoadBoundary: true,
    },
    // Auto Email Database Backup
    autoEmailBackupEnabled: false,
    autoEmailBackupIntervalMinutes: 360,
  });
  
  const [loading, setLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [notification, setNotification] = useState<NotificationState>({
    isVisible: false,
    type: 'success',
    message: ''
  });
  const [apiTestResult, setApiTestResult] = useState<{success: boolean; message: string} | null>(null);
  const [emailTestResult, setEmailTestResult] = useState<{success: boolean; message: string} | null>(null);
  const [testingApi, setTestingApi] = useState<boolean>(false);
  const [testingEmail, setTestingEmail] = useState<boolean>(false);
  const [showSuccess, setShowSuccess] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<string>(TABS[0].id);
  const [mandatorySubTab, setMandatorySubTab] = useState<'page1' | 'page2'>('page1');
  const [dbExporting, setDbExporting] = useState<boolean>(false);
  const [dbImporting, setDbImporting] = useState<boolean>(false);
  const [dbOperationResult, setDbOperationResult] = useState<{success: boolean; message: string} | null>(null);
  const [autoBackupStatus, setAutoBackupStatus] = useState<{
    enabled: boolean;
    intervalMinutes: number;
    lastSentAt: string | null;
    schedulerRunning: boolean;
    adminEmails: string[];
  } | null>(null);
  const [savingAutoBackup, setSavingAutoBackup] = useState<boolean>(false);
  const [sendingTestBackup, setSendingTestBackup] = useState<boolean>(false);
  const [testingDocRequest, setTestingDocRequest] = useState<boolean>(false);
  const [docRequestTestResult, setDocRequestTestResult] = useState<{success: boolean; message: string} | null>(null);
  
  // Purge applications state
  const [purgeType, setPurgeType] = useState<'all' | 'dateRange' | 'byApplicationId'>('dateRange');
  const [purgeStartDate, setPurgeStartDate] = useState<string>('');
  const [purgeEndDate, setPurgeEndDate] = useState<string>('');
  const [purgeApplicationId, setPurgeApplicationId] = useState<string>('');
  const [createBackup, setCreateBackup] = useState<boolean>(true);
  const [purgePreview, setPurgePreview] = useState<{ count: number; applications: any[] } | null>(null);
  const [isPurging, setIsPurging] = useState<boolean>(false);
  const [isLoadingPreview, setIsLoadingPreview] = useState<boolean>(false);
  const [purgeResult, setPurgeResult] = useState<{ success: boolean; message: string } | null>(null);
  const [purgeBackups, setPurgeBackups] = useState<any[]>([]);
  const [isLoadingBackups, setIsLoadingBackups] = useState<boolean>(false);
  const [isRestoring, setIsRestoring] = useState<boolean>(false);
  
  // Appendix Templates state
  const [appendixTemplates, setAppendixTemplates] = useState<{
    appendixA?: string;
    appendixB?: string;
    appendixC?: string;
    appendixD?: string;
    appendixE?: string;
  }>({});
  
  // Modal state for viewing appendix templates
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [viewModalUrl, setViewModalUrl] = useState('');

  // Fetch appendix templates on mount
  useEffect(() => {
    const fetchAppendixTemplates = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/appendix-templates`);
        if (response.ok) {
          const data = await response.json();
          setAppendixTemplates(data);
        }
      } catch (error) {
        console.error('Error fetching appendix templates:', error);
      }
    };
    fetchAppendixTemplates();
  }, []);

  // Handle appendix template upload
  const handleAppendixTemplateUpload = async (appendixKey: string, e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files?.[0]) return;
    
    const file = e.target.files[0];
    const formData = new FormData();
    formData.append('template', file);
    formData.append('appendixKey', appendixKey);
    
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/appendix-templates/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      if (response.ok) {
        const data = await response.json();
        setAppendixTemplates(prev => ({
          ...prev,
          [`appendix${appendixKey}`]: data.filePath
        }));
        showNotification('success', `Appendix ${appendixKey} template uploaded successfully!`);
      } else {
        showNotification('error', 'Failed to upload template');
      }
    } catch (error) {
      console.error('Error uploading appendix template:', error);
      showNotification('error', 'Error uploading template');
    }
    
    // Reset file input
    e.target.value = '';
  };
  
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;
    const newValue = type === 'checkbox' ? (e.target as HTMLInputElement).checked : value;
    
    setSettings(prev => ({
      ...prev,
      [name]: newValue
    }));
  };
  
  const showNotification = (type: 'success' | 'error' | 'info' | 'warning', message: string) => {
    setNotification({
      isVisible: true,
      type,
      message
    });
  };

  // Database Export Handler
  const handleDatabaseExport = async () => {
    setDbExporting(true);
    setDbOperationResult(null);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/database/export`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Export failed');
      }
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `database_backup_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      setDbOperationResult({ success: true, message: 'Database exported successfully!' });
      showNotification('success', 'Database exported successfully!');
    } catch (err: any) {
      setDbOperationResult({ success: false, message: err.message || 'Export failed' });
      showNotification('error', err.message || 'Database export failed');
    } finally {
      setDbExporting(false);
    }
  };

  // Database Import Handler
  const handleDatabaseImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.endsWith('.json')) {
      showNotification('error', 'Please select a valid JSON backup file');
      return;
    }

    const confirmImport = window.confirm(
      'WARNING: This will replace ALL existing data in the database with the imported data. This action cannot be undone. Are you sure you want to proceed?'
    );
    
    if (!confirmImport) {
      event.target.value = '';
      return;
    }

    setDbImporting(true);
    setDbOperationResult(null);
    
    try {
      const formData = new FormData();
      formData.append('backup', file);
      
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/database/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.message || 'Import failed');
      }
      
      setDbOperationResult({ success: true, message: result.message || 'Database imported successfully!' });
      showNotification('success', result.message || 'Database imported successfully!');
    } catch (err: any) {
      setDbOperationResult({ success: false, message: err.message || 'Import failed' });
      showNotification('error', err.message || 'Database import failed');
    } finally {
      setDbImporting(false);
      event.target.value = '';
    }
  };

  // Auto Email Backup - Fetch status
  const fetchAutoBackupStatus = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/database/auto-backup-status`);
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setAutoBackupStatus(data);
        }
      }
    } catch (err) {
      console.error('Failed to fetch auto backup status:', err);
    }
  };

  // Auto Email Backup - Save settings
  const handleSaveAutoBackupSettings = async () => {
    setSavingAutoBackup(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          autoEmailBackupEnabled: settings.autoEmailBackupEnabled,
          autoEmailBackupIntervalMinutes: settings.autoEmailBackupIntervalMinutes
        })
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showNotification('success', settings.autoEmailBackupEnabled
          ? `Auto backup enabled: every ${settings.autoEmailBackupIntervalMinutes >= 60 ? (settings.autoEmailBackupIntervalMinutes / 60) + ' hour(s)' : settings.autoEmailBackupIntervalMinutes + ' minutes'}`
          : 'Auto backup disabled');
        fetchAutoBackupStatus();
      } else {
        showNotification('error', result.message || 'Failed to save auto backup settings');
      }
    } catch (err) {
      showNotification('error', 'Failed to save auto backup settings');
    } finally {
      setSavingAutoBackup(false);
    }
  };

  // Auto Email Backup - Send test backup now
  const handleSendTestBackup = async () => {
    setSendingTestBackup(true);
    try {
      const response = await fetch(`${API_BASE}/api/admin/database/send-backup-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' }
      });
      const result = await response.json();
      if (response.ok && result.success) {
        showNotification('success', result.message || 'Backup email sent!');
        fetchAutoBackupStatus();
      } else {
        showNotification('error', result.message || 'Failed to send backup email');
      }
    } catch (err) {
      showNotification('error', 'Failed to send backup email');
    } finally {
      setSendingTestBackup(false);
    }
  };

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Check file type
    if (!file.type.match('image.*')) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'Please select an image file (PNG, JPG, SVG, etc.).'
      });
      return;
    }

    // Check file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setNotification({
        isVisible: true,
        type: 'error',
        message: 'File size should not exceed 2MB.'
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async (e) => {
      const result = e.target?.result as string;
      setLogoPreview(result);
    };
    reader.readAsDataURL(file);
  };

  const handleRemoveLogo = async () => {
    setLogoPreview(null);
    setSettings(prev => ({
      ...prev,
      logoUrl: '/assets/images/logo-new.svg'
    }));
  };

  const handleSaveSettings = async () => {
    setLoading(true);
    setError(null);

    // Prepare payload, ensuring not to send placeholder passwords if unchanged
    const payload: Partial<SettingsState> = { ...settings };
    if (settings.smtpPassword === '********') {
      delete payload.smtpPassword; // Don't send placeholder back if it wasn't changed by user
    }

    // These are for display only, derived from ENV vars on backend, not saved from client
    delete payload.razorpayKeySecretConfigured;
    delete payload.razorpayWebhookSecretConfigured;

    // Convert port to string if it's a number, or ensure it's a string
    if (payload.smtpPort && typeof payload.smtpPort !== 'string') {
        payload.smtpPort = String(payload.smtpPort);
    }

    try {
      const response = await fetch(`${API_BASE}/api/admin/settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // Add authorization headers if required by your backend
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        showNotification('success', result.message || 'Settings saved successfully!');
        // Optionally re-fetch settings to get any server-side transformations (like new placeholders)
        // Or update specific fields if server returns them (e.g., if smtpPassword was newly set, it's now '********')
        if (payload.smtpPassword && payload.smtpPassword !== '********') {
            setSettings(prev => ({...prev, smtpPassword: '********'}));
        }
      } else {
        setError(result.message || 'Failed to save settings.');
        showNotification('error', result.message || 'Failed to save settings.');
      }
    } catch (err) {
      console.error('Failed to save settings:', err);
      setError('An error occurred while saving settings.');
      showNotification('error', 'An error occurred while saving settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleTestTinyPngApi = async () => {
    setTestingApi(true);
    setApiTestResult(null);

    // Validate API key
    if (!settings.tinyPngApiKey || settings.tinyPngApiKey.length < 10) {
      setApiTestResult({
        success: false,
        message: 'Invalid API key. Please provide a valid TinyPNG API key.'
      });
      setTestingApi(false);
      return;
    }

    try {
      // Call the backend API to test the TinyPNG API key
      const response = await fetch(`${API_BASE}/api/test-tinypng`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          apiKey: settings.tinyPngApiKey
        }),
      });

      const result = await response.json();

      setApiTestResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      console.error('Error testing TinyPNG API:', error);
      setApiTestResult({
        success: false,
        message: `Error: ${error instanceof Error ? error.message : 'Failed to connect to API server'}`
      });
    } finally {
      setTestingApi(false);
    }
  };

  const handleTestSmtp = async () => {
    // Validate required fields
    if (!settings.adminEmail) {
      alert('Please enter a recipient email address for testing');
      return;
    }

    // Validate SMTP settings
    if (!settings.smtpHost || !settings.smtpPort || !settings.smtpUsername || 
        !settings.smtpPassword || !settings.smtpFromEmail) {
      alert('Please fill in all required SMTP fields before testing');
      return;
    }

    setTestingEmail(true);
    setEmailTestResult(null);

    try {
      // First, save the current settings to ensure they're in the database
      try {
        const saveResponse = await fetch(`${API_BASE}/api/admin/settings`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            ...settings,
            smtpPort: settings.smtpPort?.toString(),
            smtpSecure: settings.smtpSecure === true,

            // Razorpay
            razorpayKeyId: settings.razorpayKeyId || '',
            razorpayKeySecretConfigured: settings.razorpayKeySecretConfigured || false,
            razorpayWebhookSecretConfigured: settings.razorpayWebhookSecretConfigured || false,
          }),
        });

        if (!saveResponse.ok) {
          console.warn('Failed to save settings before testing:', await saveResponse.text());
        } else {
          console.log('Settings saved successfully before testing');
        }
      } catch (saveError) {
        console.error('Error saving settings before testing:', saveError);
      }

      // Send test email using the MongoDB-based API endpoint
      const response = await fetch(`${API_BASE}/api/test-email-settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          recipientEmail: settings.adminEmail // Send to the specified admin email
        }),
      });

      let errorMessage = '';
      let responseData;

      try {
        // Try to parse as JSON first
        responseData = await response.json();
        console.log('Test email API response:', responseData);

        if (!response.ok) {
          errorMessage = responseData.message || `Server error: ${response.status} ${response.statusText}`;
        }
      } catch (jsonError) {
        // If not JSON, get as text
        const errorText = await response.text();
        console.error('Failed to send test email (not JSON):', response.status, response.statusText, errorText);
        errorMessage = `Server error: ${response.status} ${response.statusText}`;
      }

      if (!response.ok) {
        setEmailTestResult({
          success: false,
          message: errorMessage
        });
        return;
      }

      setEmailTestResult({
        success: responseData.success,
        message: responseData.message || `Test email sent successfully to ${settings.adminEmail}`
      });
    } catch (error) {
      console.error('Error testing email settings:', error);
      setEmailTestResult({
        success: false,
        message: 'Error connecting to the backend server. Please ensure the server is running on port 5000.'
      });
    } finally {
      setTestingEmail(false);
    }
  };

  const handleTestDocumentRequest = async () => {
    setTestingDocRequest(true);
    setDocRequestTestResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/document-request/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      const result = await response.json();
      setDocRequestTestResult({
        success: result.success,
        message: result.message
      });
    } catch (error) {
      console.error('Error testing document request feature:', error);
      setDocRequestTestResult({
        success: false,
        message: 'Error connecting to the backend server.'
      });
    } finally {
      setTestingDocRequest(false);
    }
  };

  // Purge applications handlers
  const handlePurgePreview = async () => {
    if (purgeType === 'dateRange' && (!purgeStartDate || !purgeEndDate)) {
      setPurgeResult({ success: false, message: 'Please select both start and end dates.' });
      return;
    }
    if (purgeType === 'byApplicationId' && !purgeApplicationId.trim()) {
      setPurgeResult({ success: false, message: 'Please enter an Application ID.' });
      return;
    }

    setIsLoadingPreview(true);
    setPurgeResult(null);
    setPurgePreview(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/applications/purge/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purgeType,
          startDate: purgeStartDate,
          endDate: purgeEndDate,
          applicationId: purgeApplicationId.trim()
        })
      });

      const result = await response.json();
      if (result.success) {
        setPurgePreview({ count: result.count, applications: result.applications });
      } else {
        setPurgeResult({ success: false, message: result.message || 'Failed to get preview.' });
      }
    } catch (error) {
      setPurgeResult({ success: false, message: 'Error connecting to server.' });
    } finally {
      setIsLoadingPreview(false);
    }
  };

  const handlePurgeApplications = async () => {
    if (!purgePreview || purgePreview.count === 0) {
      setPurgeResult({ success: false, message: 'No applications to purge.' });
      return;
    }

    const confirmMsg = `Are you sure you want to permanently delete ${purgePreview.count} application(s) and their related documents?${createBackup ? ' A backup will be created.' : ' NO BACKUP will be created - this action is IRREVERSIBLE!'}`;
    if (!window.confirm(confirmMsg)) {
      return;
    }

    setIsPurging(true);
    setPurgeResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/applications/purge`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          purgeType,
          startDate: purgeStartDate,
          endDate: purgeEndDate,
          applicationId: purgeApplicationId.trim(),
          createBackup
        })
      });

      const result = await response.json();
      setPurgeResult({ success: result.success, message: result.message });
      if (result.success) {
        setPurgePreview(null);
        setPurgeApplicationId('');
        fetchPurgeBackups();
      }
    } catch (error) {
      setPurgeResult({ success: false, message: 'Purge operation failed.' });
    } finally {
      setIsPurging(false);
    }
  };

  const fetchPurgeBackups = async () => {
    setIsLoadingBackups(true);
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/applications/purge/backups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const result = await response.json();
      if (result.success) {
        setPurgeBackups(result.backups);
      }
    } catch (error) {
      console.error('Error fetching backups:', error);
    } finally {
      setIsLoadingBackups(false);
    }
  };

  const handleRestoreBackup = async (filename: string) => {
    if (!window.confirm(`Restore applications from backup "${filename}"? This will re-add deleted applications that don't already exist.`)) {
      return;
    }

    setIsRestoring(true);
    setPurgeResult(null);

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/applications/purge/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ filename })
      });

      const result = await response.json();
      setPurgeResult({ success: result.success, message: result.message });
    } catch (error) {
      setPurgeResult({ success: false, message: 'Restore operation failed.' });
    } finally {
      setIsRestoring(false);
    }
  };

  const handleDeleteBackup = async (filename: string) => {
    if (!window.confirm(`Delete backup "${filename}"? This cannot be undone.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${API_BASE}/api/admin/applications/purge/backups/${encodeURIComponent(filename)}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const result = await response.json();
      if (result.success) {
        fetchPurgeBackups();
        setPurgeResult({ success: true, message: 'Backup deleted.' });
      } else {
        setPurgeResult({ success: false, message: result.message });
      }
    } catch (error) {
      setPurgeResult({ success: false, message: 'Failed to delete backup.' });
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  useEffect(() => {
    const fetchSettings = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/api/admin/settings`); // Ensure this matches your API endpoint
        if (!response.ok) {
          const errorData = await response.text();
          throw new Error(`Failed to fetch settings: ${response.status} ${errorData}`);
        }
        const data = await response.json();
        const fetchedSettings = data.settings || data; // Adapt to your backend response structure

        setSettings(prevSettings => ({
          ...prevSettings,
          ...fetchedSettings,
          companyName: fetchedSettings.companyName || '',
          adminEmail: fetchedSettings.adminEmail || '',
          notificationsEnabled: fetchedSettings.notificationsEnabled === true, // Ensure boolean
          applicationAutoApproval: fetchedSettings.applicationAutoApproval === true, // Ensure boolean
          maxFileSize: fetchedSettings.maxFileSize || '10',
          defaultApplicationStatus: fetchedSettings.defaultApplicationStatus || 'pending',
          maintenanceMode: fetchedSettings.maintenanceMode === true, // Ensure boolean
          applicationsClosed: fetchedSettings.applicationsClosed === true, // Ensure boolean
          applicationDeadline: fetchedSettings.applicationDeadline ? new Date(fetchedSettings.applicationDeadline).toISOString().slice(0, 16) : '',
          emailNotifications: fetchedSettings.emailNotifications === true, // Ensure boolean
          requireOTP: fetchedSettings.requireOTP === true, // Ensure boolean
          logoUrl: fetchedSettings.logoUrl || getLogoUrlSync(),
          tinyPngApiKey: fetchedSettings.tinyPngApiKey || '',
          tinyPngCompressImages: fetchedSettings.tinyPngCompressImages === true, // Ensure boolean
          smtpHost: fetchedSettings.smtpHost || '',
          smtpPort: fetchedSettings.smtpPort ? String(fetchedSettings.smtpPort) : '',
          smtpUsername: fetchedSettings.smtpUsername || '',
          smtpPassword: fetchedSettings.smtpPassword || '', // Will be '********' if set
          smtpFromEmail: fetchedSettings.smtpFromEmail || '',
          smtpFromName: fetchedSettings.smtpFromName || '',
          smtpSecure: fetchedSettings.smtpSecure === true, // Ensure boolean
          razorpayKeyId: fetchedSettings.razorpayKeyId || '',
          razorpayKeySecretConfigured: fetchedSettings.razorpayKeySecretConfigured === true, // Ensure boolean
          razorpayWebhookSecretConfigured: fetchedSettings.razorpayWebhookSecretConfigured === true, // Ensure boolean
          documentRequestFeatureEnabled: fetchedSettings.documentRequestFeatureEnabled !== false, // Default true
          hidePaymentButton: fetchedSettings.hidePaymentButton === true, // Important Settings - Hide Payment Button
          paymentAmount: fetchedSettings.paymentAmount || 5000, // Important Settings - Payment Amount
          // Auto Email Database Backup
          autoEmailBackupEnabled: fetchedSettings.autoEmailBackupEnabled === true,
          autoEmailBackupIntervalMinutes: fetchedSettings.autoEmailBackupIntervalMinutes || 360,
        }));
        showNotification('info', 'Settings loaded.');
      } catch (error) {
        console.error('Error fetching settings:', error);
        showNotification('error', error instanceof Error ? error.message : 'Failed to load settings.');
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
    fetchAutoBackupStatus();
  }, []); // Empty dependency array means this runs once on mount

  // Auto-dismiss database operation result after 3 seconds (industry standard for success toasts)
  useEffect(() => {
    if (dbOperationResult?.success) {
      const timer = setTimeout(() => {
        setDbOperationResult(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [dbOperationResult]);

  return (
    <SettingsContainer>
      <SettingsHeader>Admin Settings</SettingsHeader>
      {notification.isVisible && (
        <Notification 
          type={notification.type} 
          message={notification.message} 
          isVisible={notification.isVisible} // Added missing isVisible prop
          onClose={() => setNotification({ ...notification, isVisible: false })}
        />
      )}

      <TabContainer>
        {TABS.map(tab => (
          <TabButton 
            key={tab.id} 
            isActive={activeTab === tab.id} 
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </TabButton>
        ))}
      </TabContainer>

      <TabContent>
        {activeTab === 'general' && (
          <>
          <SettingsCard>
            <SectionTitle>General Settings</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <Label htmlFor="companyName">Company Name</Label>
                <Input
                  type="text"
                  id="companyName"
                  name="companyName"
                  value={settings.companyName}
                  onChange={handleChange}
                  placeholder="Your Company LLC"
                />
                <HelperText>The official name of your dealership or company.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="adminEmail">Administrator Email</Label>
                <Input
                  type="email"
                  id="adminEmail"
                  name="adminEmail"
                  value={settings.adminEmail}
                  onChange={handleChange}
                  placeholder="admin@example.com"
                />
                <HelperText>The primary email address for administrative notifications and contact.</HelperText>
              </FormGroup>
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save General Settings'}
              </SaveButton>
            </SettingsForm>
          </SettingsCard>

          {/* Appendix Templates Section */}
          <SettingsCard>
            <SectionTitle>Appendix Templates</SectionTitle>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Upload the Appendix templates that applicants can download. These should be blank templates for APPENDIX A through E 
              that applicants will print on Rs. 500 Stamp Paper, fill, notarize, and upload back.
            </p>
            
            {['A', 'B', 'C', 'D', 'E'].map((appendixKey) => (
              <FormGroup key={appendixKey} style={{ 
                padding: '1rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '8px', 
                marginBottom: '1rem',
                border: '1px solid #e5e7eb'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                  <Label style={{ margin: 0, fontSize: '1rem', fontWeight: 600 }}>
                    APPENDIX - {appendixKey}
                  </Label>
                  {(appendixTemplates as any)[`appendix${appendixKey}`] && (
                    <span style={{ color: '#10b981', fontSize: '0.85rem' }}>✓ Uploaded</span>
                  )}
                </div>
                <HelperText style={{ margin: '0.25rem 0 0.75rem 0' }}>
                  {appendixKey === 'A' && 'Affidavit Cum Undertaking template'}
                  {appendixKey === 'B' && 'Land Owner Consent / NOC template'}
                  {appendixKey === 'C' && 'Partnership Deed / Authorization template'}
                  {appendixKey === 'D' && 'Power of Attorney template'}
                  {appendixKey === 'E' && 'Additional Declarations template'}
                </HelperText>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  <FileUploadButton htmlFor={`appendix${appendixKey}Template`}>
                    📤 {(appendixTemplates as any)[`appendix${appendixKey}`] ? 'Replace' : 'Upload'} Template
                  </FileUploadButton>
                  <input
                    type="file"
                    id={`appendix${appendixKey}Template`}
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={(e) => handleAppendixTemplateUpload(appendixKey, e)}
                  />
                  {(appendixTemplates as any)[`appendix${appendixKey}`] && (
                    <Button
                      type="button"
                      onClick={() => {
                        setViewModalUrl(`${API_BASE}${(appendixTemplates as any)[`appendix${appendixKey}`]}`);
                        setViewModalOpen(true);
                      }}
                      style={{ backgroundColor: '#10b981', padding: '0.5rem 1rem', fontSize: '0.85rem' }}
                    >
                      👁 View
                    </Button>
                  )}
                </div>
              </FormGroup>
            ))}
          </SettingsCard>
          </>
        )}

        {activeTab === 'important' && (
          <SettingsCard>
            <SectionTitle>Important Settings</SectionTitle>
            <p style={{ color: '#4b5563', marginBottom: '1.5rem', lineHeight: '1.6' }}>
              Critical settings that affect application functionality. Changes take effect immediately for all users without requiring page refresh.
            </p>
            
            <div style={{ 
              backgroundColor: '#fef3c7', 
              border: '1px solid #f59e0b', 
              borderRadius: '8px', 
              padding: '1rem', 
              marginBottom: '1.5rem' 
            }}>
              <p style={{ color: '#92400e', margin: 0, fontSize: '0.9rem' }}>
                <strong>⚠️ Note:</strong> These settings affect all users in real-time. Use with caution.
              </p>
            </div>

            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup style={{ 
                padding: '1.5rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '12px', 
                border: '1px solid #e5e7eb',
                marginBottom: '1rem'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.75rem' }}>
                  <div>
                    <Label style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                      🚫 Hide "PROCEED TO PAYMENT" Button
                    </Label>
                    <HelperText style={{ margin: 0 }}>
                      When enabled, the payment button will be hidden on Page 5 (Final Review) for all applicants. 
                      A message will inform users that payments are temporarily disabled.
                    </HelperText>
                  </div>
                  <ToggleSwitchContainer>
                    <input 
                      type="checkbox" 
                      name="hidePaymentButton" 
                      checked={settings.hidePaymentButton} 
                      onChange={handleChange} 
                    />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </div>
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: settings.hidePaymentButton ? '#fee2e2' : '#dcfce7', 
                  borderRadius: '6px',
                  border: `1px solid ${settings.hidePaymentButton ? '#fecaca' : '#bbf7d0'}`
                }}>
                  <span style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500, 
                    color: settings.hidePaymentButton ? '#dc2626' : '#16a34a' 
                  }}>
                    {settings.hidePaymentButton 
                      ? '🔴 Payment button is currently HIDDEN for all users' 
                      : '🟢 Payment button is currently VISIBLE for all users'}
                  </span>
                </div>
              </FormGroup>
              
              <FormGroup style={{ 
                padding: '1.5rem', 
                backgroundColor: '#f9fafb', 
                borderRadius: '12px', 
                border: '1px solid #e5e7eb',
                marginBottom: '1rem'
              }}>
                <div style={{ marginBottom: '0.75rem' }}>
                  <Label style={{ fontSize: '1rem', fontWeight: 600, marginBottom: '0.25rem', display: 'block' }}>
                    💰 Application Fee Amount (₹)
                  </Label>
                  <HelperText style={{ margin: 0 }}>
                    The amount displayed on the payment page that applicants need to pay. This value is used in the payment flow.
                  </HelperText>
                </div>
                <Input
                  type="number"
                  id="paymentAmount"
                  name="paymentAmount"
                  value={settings.paymentAmount}
                  onChange={handleChange}
                  min="1"
                  placeholder="5000"
                  style={{ maxWidth: '200px' }}
                />
                <div style={{ 
                  marginTop: '1rem', 
                  padding: '0.75rem', 
                  backgroundColor: '#dbeafe', 
                  borderRadius: '6px',
                  border: '1px solid #93c5fd'
                }}>
                  <span style={{ 
                    fontSize: '0.85rem', 
                    fontWeight: 500, 
                    color: '#1e40af' 
                  }}>
                    💵 Current payment amount: ₹{settings.paymentAmount?.toLocaleString('en-IN') || '5,000'}
                  </span>
                </div>
              </FormGroup>
              
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Important Settings'}
              </SaveButton>
            </SettingsForm>
          </SettingsCard>
        )}

        {activeTab === 'mandatory' && (
          <SettingsCard>
            {/* Sub-tabs for Page 1 and Page 2 */}
            <div style={{ 
              display: 'flex', 
              gap: '0.5rem', 
              marginBottom: '1rem',
              borderBottom: '2px solid #e2e8f0',
              paddingBottom: '0.5rem'
            }}>
              <button
                type="button"
                onClick={() => setMandatorySubTab('page1')}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  background: mandatorySubTab === 'page1' ? 'linear-gradient(135deg, #4361ee 0%, #3a56d4 100%)' : '#f1f5f9',
                  color: mandatorySubTab === 'page1' ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📄 Page 1
              </button>
              <button
                type="button"
                onClick={() => setMandatorySubTab('page2')}
                style={{
                  padding: '0.5rem 1rem',
                  border: 'none',
                  borderRadius: '6px 6px 0 0',
                  background: mandatorySubTab === 'page2' ? 'linear-gradient(135deg, #4361ee 0%, #3a56d4 100%)' : '#f1f5f9',
                  color: mandatorySubTab === 'page2' ? '#fff' : '#64748b',
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                📄 Page 2
              </button>
            </div>

            {/* PAGE 1 MANDATORY FIELDS */}
            {mandatorySubTab === 'page1' && (
              <>
                <SectionTitle style={{ marginBottom: '0.5rem' }}>Mandatory Fields (Page 1)</SectionTitle>
                <p style={{ color: '#64748b', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                  Toggle OFF to make a field optional. Applies to Main Applicant and Partners/Directors.
                </p>
                
                <div style={{ 
                  backgroundColor: '#dbeafe', 
                  border: '1px solid #93c5fd', 
                  borderRadius: '6px', 
                  padding: '0.5rem 0.75rem', 
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '0.85rem' }}>ℹ️</span>
                  <span style={{ color: '#1e40af', fontSize: '0.75rem' }}>Changes take effect immediately. Fields marked mandatory show "Required" indicator.</span>
                </div>

                <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
                  {/* Two-Column Layout */}
                  <div style={{ 
                    display: 'grid', 
                    gridTemplateColumns: '1fr 1fr', 
                    gap: '0.75rem',
                    marginBottom: '0.75rem'
                  }}>
                    {/* Left Column */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                      {[
                        { num: 1, icon: '📷', label: 'Profile Photo', key: 'profilePhoto' },
                        { num: 2, icon: '📢', label: 'Advertisement Source', key: 'advertisementSource' },
                        { num: 3, icon: '📍', label: 'CNG Station Location', key: 'cngStationLocation' },
                        { num: 4, icon: '📋', label: 'Application Type', key: 'applicationType' },
                        { num: 5, icon: '💳', label: 'PAN Card Upload', key: 'panCardUpload' },
                        { num: 6, icon: '🎓', label: 'Min. Class 10 Pass', key: 'minimumEducation' },
                        { num: 7, icon: '🏠', label: 'Current Address', key: 'currentAddress' },
                        { num: 8, icon: '📱', label: 'Personal Mobile', key: 'personalMobile' },
                      ].map((field, idx) => (
                        <div key={field.key} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '0.4rem 0.6rem', 
                          borderBottom: idx < 7 ? '1px solid #f1f5f9' : 'none',
                          background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                            <span>{field.icon}</span>
                            <span>{field.label}</span>
                          </span>
                          <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                            <input 
                              type="checkbox" 
                              checked={(settings.mandatoryFields as any)?.[field.key] ?? true} 
                              onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFields: { ...prev.mandatoryFields, [field.key]: e.target.checked } }))} 
                            />
                            <ToggleSlider />
                          </ToggleSwitchContainer>
                        </div>
                      ))}
                    </div>

                    {/* Right Column */}
                    <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden', background: '#fff' }}>
                      {[
                        { num: 9, icon: '🌍', label: 'Nationality', key: 'nationality' },
                        { num: 10, icon: '💍', label: 'Marital Status', key: 'maritalStatus' },
                        { num: 11, icon: '👤', label: 'Gender', key: 'gender' },
                        { num: 12, icon: '🔐', label: 'KYC (Aadhaar & PAN)', key: 'kycAadhaarPan' },
                        { num: 13, icon: '📚', label: 'Education Qual.', key: 'educationalQualification' },
                        { num: 14, icon: '💼', label: 'Business Details', key: 'businessEmploymentDetails' },
                        { num: 15, icon: '💰', label: 'Financial Capability', key: 'financialCapability' },
                        { num: 16, icon: '📄', label: 'Financial Docs', key: 'financialCapabilityDocChecklist' },
                      ].map((field, idx) => (
                        <div key={field.key} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '0.4rem 0.6rem', 
                          borderBottom: idx < 7 ? '1px solid #f1f5f9' : 'none',
                          background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                            <span>{field.icon}</span>
                            <span>{field.label}</span>
                          </span>
                          <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                            <input 
                              type="checkbox" 
                              checked={(settings.mandatoryFields as any)?.[field.key] ?? true} 
                              onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFields: { ...prev.mandatoryFields, [field.key]: e.target.checked } }))} 
                            />
                            <ToggleSlider />
                          </ToggleSwitchContainer>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Appendix Row - Full Width */}
                  <div style={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'space-between',
                    padding: '0.5rem 0.75rem', 
                    borderRadius: '8px',
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    border: '1px solid #f59e0b'
                  }}>
                    <span style={{ fontSize: '0.8rem', color: '#92400e', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span>📑</span>
                      <span>Appendix Cards (A-F) - All visible cards must be filled</span>
                    </span>
                    <ToggleSwitchContainer style={{ transform: 'scale(0.8)' }}>
                      <input 
                        type="checkbox" 
                        checked={settings.mandatoryFields?.appendixCards ?? true} 
                        onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFields: { ...prev.mandatoryFields, appendixCards: e.target.checked } }))} 
                      />
                      <ToggleSlider />
                    </ToggleSwitchContainer>
                  </div>
                  
                  <SaveButton type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
                    {loading ? 'Saving...' : 'Save Settings'}
                  </SaveButton>
                </SettingsForm>
              </>
            )}

            {/* PAGE 2 MANDATORY FIELDS */}
            {mandatorySubTab === 'page2' && (
              <>
                <SectionTitle style={{ marginBottom: '0.5rem' }}>Mandatory Fields (Page 2)</SectionTitle>
                <p style={{ color: '#64748b', marginBottom: '0.75rem', fontSize: '0.8rem' }}>
                  Toggle OFF to make a field optional. Controls Land/Plot ownership and related details.
                </p>
                
                <div style={{ 
                  backgroundColor: '#dbeafe', 
                  border: '1px solid #93c5fd', 
                  borderRadius: '6px', 
                  padding: '0.5rem 0.75rem', 
                  marginBottom: '1rem',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}>
                  <span style={{ fontSize: '0.85rem' }}>ℹ️</span>
                  <span style={{ color: '#1e40af', fontSize: '0.75rem' }}>Changes take effect immediately. Fields marked mandatory show "Required" indicator.</span>
                </div>

                <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
                  {/* SECTION 1: OFFERED LAND/PLOT OWNERSHIP DETAILS */}
                  <div style={{ 
                    marginBottom: '1rem',
                    border: '1px solid #3498db',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #3498db 0%, #2980b9 100%)',
                      padding: '0.5rem 0.75rem',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.8rem'
                    }}>
                      🏠 OFFERED LAND/PLOT OWNERSHIP DETAILS (MANDATORY)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                      {/* Left Column */}
                      <div style={{ borderRight: '1px solid #e2e8f0' }}>
                        {[
                          { num: 1, icon: '📋', label: 'Ownership Status', key: 'landOwnershipStatus' },
                          { num: 2, icon: '📅', label: 'Period of Land Lease', key: 'periodOfLease' },
                          { num: 3, icon: '📎', label: 'Attach Offered Plot Drawing', key: 'landPlotDrawingFile' },
                          { num: 4, icon: '📍', label: 'Complete Address of Offered Land/Plot', key: 'landCompleteAddress' },
                          { num: 5, icon: '🗺️', label: 'Location Google Map Coordinates/Link', key: 'landLocationCoordinates' },
                          { num: 6, icon: '📝', label: 'Survey No: / Gata No:', key: 'landSurveyNo' },
                          { num: 7, icon: '📝', label: 'Khata No:', key: 'landKhataNo' },
                          { num: 8, icon: '📝', label: 'Khatauni No:', key: 'landKhatauniNo' },
                        ].map((field, idx) => (
                          <div key={field.key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.4rem 0.6rem', 
                            borderBottom: '1px solid #f1f5f9',
                            background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                          }}>
                            <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                              <span>{field.icon}</span>
                              <span>{field.label}</span>
                            </span>
                            <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                              <input 
                                type="checkbox" 
                                checked={(settings.mandatoryFieldsPage2 as any)?.[field.key] ?? true} 
                                onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, [field.key]: e.target.checked } }))} 
                              />
                              <ToggleSlider />
                            </ToggleSwitchContainer>
                          </div>
                        ))}
                      </div>
                      {/* Right Column */}
                      <div>
                        {[
                          { num: 9, icon: '📝', label: 'Ferfar No:', key: 'landFerfarNo' },
                          { num: 10, icon: '📝', label: 'Varga No:', key: 'landVargaNo' },
                          { num: 11, icon: '📏', label: 'Frontage (Mtrs) to MNGL', key: 'mnglLandFrontageMtrs' },
                          { num: 12, icon: '📏', label: 'Depth (Mtrs) to MNGL', key: 'mnglLandDepthMtrs' },
                          { num: 13, icon: '📐', label: 'Total Area (Sq. Mtrs) to MNGL', key: 'mnglTotalAreaSqMtrs' },
                          { num: 14, icon: '🛣️', label: 'Approach Road Width (Mtrs)', key: 'landApproachRoadWidthMtrs' },
                          { num: 15, icon: '📊', label: 'Plot Level from Road', key: 'landPlotLevelType' },
                        ].map((field, idx) => (
                          <div key={field.key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.4rem 0.6rem', 
                            borderBottom: '1px solid #f1f5f9',
                            background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                          }}>
                            <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                              <span>{field.icon}</span>
                              <span>{field.label}</span>
                            </span>
                            <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                              <input 
                                type="checkbox" 
                                checked={(settings.mandatoryFieldsPage2 as any)?.[field.key] ?? true} 
                                onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, [field.key]: e.target.checked } }))} 
                              />
                              <ToggleSlider />
                            </ToggleSwitchContainer>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* SECTION 2: SURROUNDINGS OF OFFERED LAND/PLOT */}
                  <div style={{ 
                    marginBottom: '1rem',
                    border: '1px solid #27ae60',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #27ae60 0%, #219a52 100%)',
                      padding: '0.5rem 0.75rem',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.8rem'
                    }}>
                      🧭 Surroundings of Offered Land/Plot
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                      {[
                        { num: 16, icon: '➡️', label: 'East', key: 'surroundingsEast' },
                        { num: 17, icon: '⬅️', label: 'West', key: 'surroundingsWest' },
                        { num: 18, icon: '⬆️', label: 'North', key: 'surroundingsNorth' },
                        { num: 19, icon: '⬇️', label: 'South', key: 'surroundingsSouth' },
                      ].map((field, idx) => (
                        <div key={field.key} style={{ 
                          display: 'flex', 
                          alignItems: 'center', 
                          justifyContent: 'space-between',
                          padding: '0.4rem 0.6rem', 
                          borderBottom: idx < 2 ? '1px solid #f1f5f9' : 'none',
                          borderRight: idx % 2 === 0 ? '1px solid #e2e8f0' : 'none',
                          background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                        }}>
                          <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                            <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                            <span>{field.icon}</span>
                            <span>{field.label}</span>
                          </span>
                          <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                            <input 
                              type="checkbox" 
                              checked={(settings.mandatoryFieldsPage2 as any)?.[field.key] ?? true} 
                              onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, [field.key]: e.target.checked } }))} 
                            />
                            <ToggleSlider />
                          </ToggleSwitchContainer>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* SECTION 3: OTHER INFORMATION (OFFERED LAND/PLOT RELATED) */}
                  <div style={{ 
                    marginBottom: '1rem',
                    border: '1px solid #9b59b6',
                    borderRadius: '8px',
                    overflow: 'hidden'
                  }}>
                    <div style={{ 
                      background: 'linear-gradient(135deg, #9b59b6 0%, #8e44ad 100%)',
                      padding: '0.5rem 0.75rem',
                      color: '#fff',
                      fontWeight: 600,
                      fontSize: '0.8rem'
                    }}>
                      📋 OTHER INFORMATION (OFFERED LAND/PLOT RELATED)
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0' }}>
                      {/* Left Column */}
                      <div style={{ borderRight: '1px solid #e2e8f0' }}>
                        {[
                          { num: 20, icon: '🌾', label: 'Offered Land/Plot Use', key: 'landUse' },
                          { num: 21, icon: '✅', label: 'Title Clear & Marketable', key: 'landTitleClear' },
                          { num: 22, icon: '⚖️', label: 'Litigation Free', key: 'landLitigationFree' },
                          { num: 23, icon: '⚡', label: 'Nearby Power Connection', key: 'landPowerConnectionAvailable' },
                          { num: 24, icon: '💧', label: 'Water Connection Available', key: 'landWaterConnectionAvailable' },
                        ].map((field, idx) => (
                          <div key={field.key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.4rem 0.6rem', 
                            borderBottom: '1px solid #f1f5f9',
                            background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                          }}>
                            <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                              <span>{field.icon}</span>
                              <span>{field.label}</span>
                            </span>
                            <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                              <input 
                                type="checkbox" 
                                checked={(settings.mandatoryFieldsPage2 as any)?.[field.key] ?? true} 
                                onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, [field.key]: e.target.checked } }))} 
                              />
                              <ToggleSlider />
                            </ToggleSwitchContainer>
                          </div>
                        ))}
                      </div>
                      {/* Right Column */}
                      <div>
                        {[
                          { num: 25, icon: '🚧', label: 'Encroachment', key: 'landEncroachment' },
                          { num: 26, icon: '🏛️', label: 'Govt. Acquisition Plan', key: 'landGovtAcquisitionPlan' },
                          { num: 27, icon: '🚔', label: 'Nearest Police Station', key: 'nearestPoliceStationName' },
                          { num: 28, icon: '🚒', label: 'Nearest Fire Station', key: 'nearestFireStationName' },
                          { num: 29, icon: '⚡', label: 'High/Low Voltage Line', key: 'highVoltageTransmissionLine' },
                        ].map((field, idx) => (
                          <div key={field.key} style={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            justifyContent: 'space-between',
                            padding: '0.4rem 0.6rem', 
                            borderBottom: '1px solid #f1f5f9',
                            background: idx % 2 === 1 ? '#fafbfc' : '#fff'
                          }}>
                            <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                              <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>{field.num}</span>
                              <span>{field.icon}</span>
                              <span>{field.label}</span>
                            </span>
                            <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                              <input 
                                type="checkbox" 
                                checked={(settings.mandatoryFieldsPage2 as any)?.[field.key] ?? true} 
                                onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, [field.key]: e.target.checked } }))} 
                              />
                              <ToggleSlider />
                            </ToggleSwitchContainer>
                          </div>
                        ))}
                      </div>
                    </div>
                    {/* Full-width last field */}
                    <div style={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'space-between',
                      padding: '0.4rem 0.6rem', 
                      background: '#fafbfc',
                      borderTop: '1px solid #e2e8f0'
                    }}>
                      <span style={{ fontSize: '0.75rem', color: '#334155', display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                        <span style={{ color: '#94a3b8', fontSize: '0.65rem', minWidth: '14px' }}>30</span>
                        <span>🛤️</span>
                        <span>Road Boundary (Right of Way)</span>
                      </span>
                      <ToggleSwitchContainer style={{ transform: 'scale(0.75)' }}>
                        <input 
                          type="checkbox" 
                          checked={(settings.mandatoryFieldsPage2 as any)?.landAbuttingRoadBoundary ?? true} 
                          onChange={(e) => setSettings(prev => ({ ...prev, mandatoryFieldsPage2: { ...prev.mandatoryFieldsPage2, landAbuttingRoadBoundary: e.target.checked } }))} 
                        />
                        <ToggleSlider />
                      </ToggleSwitchContainer>
                    </div>
                  </div>
                  
                  <SaveButton type="submit" disabled={loading} style={{ marginTop: '1rem' }}>
                    {loading ? 'Saving...' : 'Save Settings'}
                  </SaveButton>
                </SettingsForm>
              </>
            )}
          </SettingsCard>
        )}

        {activeTab === 'database' && (
          <SettingsCard>
            <SectionTitle>Database Settings</SectionTitle>
            <div style={{ marginBottom: '2rem' }}>
              <p style={{ color: '#4b5563', marginBottom: '1rem', lineHeight: '1.6' }}>
                Manage your database backups. Export creates a complete backup of all data including applications, users, settings, and file references.
                Import will replace all existing data with the backup file contents.
              </p>
              <div style={{ backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px', padding: '1rem', marginBottom: '1.5rem' }}>
                <p style={{ color: '#92400e', margin: 0, fontSize: '0.9rem' }}>
                  <strong>⚠️ Warning:</strong> Importing a database backup will permanently replace all existing data. This action cannot be undone. Always export a backup before importing.
                </p>
              </div>
            </div>

            <FormGroup>
              <Label>Export Database</Label>
              <HelperText>Download a complete backup of the database as a JSON file.</HelperText>
              <SaveButton 
                type="button" 
                onClick={handleDatabaseExport} 
                disabled={dbExporting}
                style={{ width: 'fit-content' }}
              >
                {dbExporting ? 'Exporting...' : '📥 Export Full Database'}
              </SaveButton>
            </FormGroup>

            <SectionDivider />

            <FormGroup>
              <Label>Import Database</Label>
              <HelperText>Restore the database from a previously exported JSON backup file.</HelperText>
              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                <FileUploadButton htmlFor="dbImportFile" style={{ backgroundColor: dbImporting ? '#9ca3af' : '#dc2626' }}>
                  {dbImporting ? 'Importing...' : '📤 Import Database Backup'}
                  <input 
                    id="dbImportFile" 
                    type="file" 
                    accept=".json" 
                    onChange={handleDatabaseImport}
                    disabled={dbImporting}
                  />
                </FileUploadButton>
              </div>
            </FormGroup>

            {dbOperationResult && (
              <div style={{
                marginTop: '1rem',
                padding: '1rem',
                borderRadius: '8px',
                backgroundColor: dbOperationResult.success ? '#d1fae5' : '#fee2e2',
                border: `1px solid ${dbOperationResult.success ? '#10b981' : '#ef4444'}`,
                color: dbOperationResult.success ? '#065f46' : '#991b1b'
              }}>
                {dbOperationResult.message}
              </div>
            )}

            <SectionDivider />

            <FormGroup>
              <Label>Auto Email Backup</Label>
              <HelperText>When enabled, the database backup JSON file will be automatically emailed to admin accounts at the configured interval.</HelperText>

              <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '0.75rem', marginBottom: '1rem' }}>
                <ToggleRow>
                  <ToggleLabel>{settings.autoEmailBackupEnabled ? 'Enabled' : 'Disabled'}</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input
                      type="checkbox"
                      checked={settings.autoEmailBackupEnabled}
                      onChange={(e) => setSettings(prev => ({ ...prev, autoEmailBackupEnabled: e.target.checked }))}
                    />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
              </div>

              {settings.autoEmailBackupEnabled && (
                <div style={{ marginBottom: '1rem' }}>
                  <Label style={{ fontSize: '0.85rem', marginBottom: '0.4rem' }}>Backup Interval</Label>
                  <Select
                    value={settings.autoEmailBackupIntervalMinutes}
                    onChange={(e) => setSettings(prev => ({ ...prev, autoEmailBackupIntervalMinutes: Number(e.target.value) }))}
                    style={{ maxWidth: '280px' }}
                  >
                    <option value={30}>Every 30 minutes</option>
                    <option value={60}>Every 1 hour</option>
                    <option value={120}>Every 2 hours</option>
                    <option value={180}>Every 3 hours</option>
                    <option value={360}>Every 6 hours</option>
                    <option value={720}>Every 12 hours</option>
                    <option value={1440}>Every 24 hours</option>
                  </Select>
                </div>
              )}

              <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                <SaveButton
                  type="button"
                  onClick={handleSaveAutoBackupSettings}
                  disabled={savingAutoBackup}
                  style={{ width: 'fit-content' }}
                >
                  {savingAutoBackup ? 'Saving...' : 'Save Auto Backup Settings'}
                </SaveButton>

                {settings.autoEmailBackupEnabled && (
                  <SaveButton
                    type="button"
                    onClick={handleSendTestBackup}
                    disabled={sendingTestBackup}
                    style={{ width: 'fit-content', backgroundColor: '#059669' }}
                  >
                    {sendingTestBackup ? 'Sending...' : '📧 Send Backup Now'}
                  </SaveButton>
                )}
              </div>

              {autoBackupStatus && (
                <div style={{
                  marginTop: '1rem',
                  padding: '0.75rem 1rem',
                  backgroundColor: '#f0f9ff',
                  border: '1px solid #bae6fd',
                  borderRadius: '8px',
                  fontSize: '0.85rem',
                  color: '#0c4a6e'
                }}>
                  <div style={{ marginBottom: '0.3rem' }}>
                    <strong>Status:</strong> {autoBackupStatus.schedulerRunning ? '🟢 Scheduler running' : '⚪ Scheduler stopped'}
                  </div>
                  <div style={{ marginBottom: '0.3rem' }}>
                    <strong>Sends to:</strong> {autoBackupStatus.adminEmails?.join(', ') || 'No admin emails configured'}
                  </div>
                  {autoBackupStatus.lastSentAt && (
                    <div>
                      <strong>Last sent:</strong> {new Date(autoBackupStatus.lastSentAt).toLocaleString('en-IN')}
                    </div>
                  )}
                </div>
              )}
            </FormGroup>
          </SettingsCard>
        )}

        {activeTab === 'branding' && (
          <SettingsCard>
            <SectionTitle>Branding & Appearance</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <Label>Company Logo</Label>
                <HelperText>Upload your company logo. Recommended format: SVG, PNG, JPG. Max size: 2MB.</HelperText>
                <LogoPreview>
                  {logoPreview ? <img src={logoPreview} alt="Logo Preview" /> : <span>No Logo Set</span>}
                </LogoPreview>
                <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <FileUploadButton htmlFor="logoUpload">
                    Upload New Logo
                    <input id="logoUpload" type="file" accept="image/*" onChange={handleLogoUpload} />
                  </FileUploadButton>
                  {logoPreview && settings.logoUrl !== '/assets/images/logo-new.svg' && (
                    <SecondaryButton onClick={handleRemoveLogo} type="button">Remove Logo</SecondaryButton>
                  )}
                </div>
              </FormGroup>
              {/* Add theme color pickers or other branding options here later */}
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Branding Settings'}
              </SaveButton>
            </SettingsForm>
          </SettingsCard>
        )}

        {activeTab === 'notifications' && (
          <SettingsCard>
            <SectionTitle>Notification Settings</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Enable All System Notifications</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="notificationsEnabled" checked={settings.notificationsEnabled} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Master switch for all system-generated notifications (excluding direct email).</HelperText>
              </FormGroup>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Email Notifications for New Applications</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="emailNotifications" checked={settings.emailNotifications} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Send an email to the admin when a new application is submitted.</HelperText>
              </FormGroup>
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Notification Settings'}
              </SaveButton>
            </SettingsForm>
          </SettingsCard>
        )}

        {activeTab === 'security' && (
          <SettingsCard>
            <SectionTitle>Security & Access</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Require OTP for Admin Login</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="requireOTP" checked={settings.requireOTP} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Enhance security by requiring a One-Time Password for admin access.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="sessionTimeoutMinutes">Session Timeout (Minutes)</Label>
                <Input
                  type="number"
                  id="sessionTimeoutMinutes"
                  name="sessionTimeoutMinutes"
                  value={settings.sessionTimeoutMinutes || 30}
                  onChange={handleChange}
                  min="5"
                  max="480"
                  placeholder="30"
                />
                <HelperText>How long (in minutes) before a user's session expires due to inactivity. Default: 30 minutes. Range: 5-480 minutes.</HelperText>
              </FormGroup>
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Security Settings'}
              </SaveButton>
            </SettingsForm>
          </SettingsCard>
        )}
        
        {activeTab === 'integrations' && (
          <SettingsCard>
            <SectionTitle>Integrations</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <Label htmlFor="tinyPngApiKey">TinyPNG API Key</Label>
                <Input
                  type="password"
                  id="tinyPngApiKey"
                  name="tinyPngApiKey"
                  value={settings.tinyPngApiKey}
                  onChange={handleChange}
                  placeholder="Enter your TinyPNG API Key"
                />
                <HelperText>Used for compressing uploaded images to save storage and bandwidth.</HelperText>
              </FormGroup>

              {/* Razorpay Settings Start Here */}
              <SectionTitle style={{ fontSize: '1.2rem', marginTop: '2rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb', borderBottom: 'none' }}>Razorpay Payment Gateway</SectionTitle>
              <FormGroup>
                <Label htmlFor="razorpayKeyId">Razorpay Key ID</Label>
                <Input
                  type="text"
                  id="razorpayKeyId"
                  name="razorpayKeyId"
                  value={settings.razorpayKeyId || ''}
                  onChange={handleChange}
                  placeholder="rzp_live_... or rzp_test_..."
                />
                <HelperText>Your Razorpay Key ID. Get this from your Razorpay dashboard. The Key Secret and Webhook Secret are managed via server environment variables.</HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Razorpay Key Secret Status</Label>
                <Input
                  type="text"
                  value={settings.razorpayKeySecretConfigured ? "Configured (Set in environment variables)" : "Not Configured (Set RAZORPAY_KEY_SECRET in .env)"}
                  readOnly
                  disabled
                  style={{
                    backgroundColor: settings.razorpayKeySecretConfigured ? 'var(--success-light, #e6fffa)' : 'var(--error-light, #fff5f5)',
                    color: settings.razorpayKeySecretConfigured ? 'var(--success-dark, #2f855a)' : 'var(--error-dark, #c53030)',
                    borderColor: settings.razorpayKeySecretConfigured ? 'var(--success-main, #38a169)' : 'var(--error-main, #e53e3e)',
                    cursor: 'not-allowed'
                  }}
                />
                <HelperText>For security, the Key Secret must be set as an environment variable (RAZORPAY_KEY_SECRET) on your server.</HelperText>
              </FormGroup>

              <FormGroup>
                <Label>Razorpay Webhook Secret Status</Label>
                <Input
                  type="text"
                  value={settings.razorpayWebhookSecretConfigured ? "Configured (Set in environment variables)" : "Not Configured (Set RAZORPAY_WEBHOOK_SECRET in .env)"}
                  readOnly
                  disabled
                  style={{
                    backgroundColor: settings.razorpayWebhookSecretConfigured ? 'var(--success-light, #e6fffa)' : 'var(--error-light, #fff5f5)',
                    color: settings.razorpayWebhookSecretConfigured ? 'var(--success-dark, #2f855a)' : 'var(--error-dark, #c53030)',
                    borderColor: settings.razorpayWebhookSecretConfigured ? 'var(--success-main, #38a169)' : 'var(--error-main, #e53e3e)',
                    cursor: 'not-allowed'
                  }}
                />
                <HelperText>For verifying webhook signatures. Set as an environment variable (RAZORPAY_WEBHOOK_SECRET) on your server. Get this from Razorpay dashboard when setting up webhooks.</HelperText>
              </FormGroup>
              {/* Razorpay Settings End Here */}
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Enable Image Compression via TinyPNG</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input 
                      type="checkbox" 
                      name="tinyPngCompressImages" 
                      checked={settings.tinyPngCompressImages} 
                      onChange={handleChange} 
                    />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Automatically compress images upon upload using your TinyPNG API key.</HelperText>
              </FormGroup>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
                <Button type="button" onClick={handleTestTinyPngApi} disabled={testingApi || !settings.tinyPngApiKey}>
                  {testingApi ? 'Testing...' : 'Test API Key'}
                </Button>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save Integration Settings'}
                </SaveButton>
              </div>
              {apiTestResult && (
                <TestResult success={apiTestResult.success} style={{ marginTop: '1rem' }}>{apiTestResult.message}</TestResult>
              )}
            </SettingsForm>
          </SettingsCard>
        )}

        {activeTab === 'smtp' && (
          <SettingsCard>
            <SectionTitle>Email (SMTP) Settings</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <Label htmlFor="smtpHost">SMTP Host</Label>
                <Input type="text" id="smtpHost" name="smtpHost" value={settings.smtpHost} onChange={handleChange} placeholder="e.g., smtp.gmail.com" />
                <HelperText>Your email provider's SMTP server address.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="smtpPort">SMTP Port</Label>
                <Input type="text" id="smtpPort" name="smtpPort" value={settings.smtpPort} onChange={handleChange} placeholder="e.g., 587 (TLS) or 465 (SSL)" />
                <HelperText>Common ports are 587 (for TLS/STARTTLS) or 465 (for SSL).</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="smtpUsername">SMTP Username</Label>
                <Input type="email" id="smtpUsername" name="smtpUsername" value={settings.smtpUsername} onChange={handleChange} placeholder="your.email@example.com" />
                <HelperText>Usually your full email address.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="smtpPassword">SMTP Password</Label>
                <PasswordInput type="password" id="smtpPassword" name="smtpPassword" value={settings.smtpPassword} onChange={handleChange} placeholder="Enter your email password or App Password" />
                <HelperText>For Gmail, use an App Password if 2FA is enabled.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="smtpFromEmail">'From' Email Address</Label>
                <Input type="email" id="smtpFromEmail" name="smtpFromEmail" value={settings.smtpFromEmail} onChange={handleChange} placeholder="noreply@yourdomain.com" />
                <HelperText>The email address system emails will be sent from.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="smtpFromName">'From' Name</Label>
                <Input type="text" id="smtpFromName" name="smtpFromName" value={settings.smtpFromName} onChange={handleChange} placeholder="Your Application Name" />
                <HelperText>The name that appears as the sender of system emails.</HelperText>
              </FormGroup>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Use Secure Connection (SSL/TLS)</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="smtpSecure" checked={settings.smtpSecure} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Enable if your SMTP server uses SSL or TLS encryption.</HelperText>
              </FormGroup>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '1rem' }}>
                <Button type="button" onClick={handleTestSmtp} disabled={testingEmail || !settings.smtpHost || !settings.smtpUsername}>
                  {testingEmail ? 'Testing...' : 'Test SMTP Connection'}
                </Button>
                <SaveButton type="submit" disabled={loading}>
                  {loading ? 'Saving...' : 'Save SMTP Settings'}
                </SaveButton>
              </div>
              {emailTestResult && (
                <TestResult success={emailTestResult.success} style={{ marginTop: '1rem' }}>{emailTestResult.message}</TestResult>
              )}
            </SettingsForm>
          </SettingsCard>
        )}

        {activeTab === 'advanced' && (
          <SettingsCard>
            <SectionTitle>Advanced Settings</SectionTitle>
            <SettingsForm onSubmit={(e) => { e.preventDefault(); handleSaveSettings(); }}>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Auto-approve new applications</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="applicationAutoApproval" checked={settings.applicationAutoApproval} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Automatically set the status of new applications to 'Approved'. Use with caution.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="maxFileSize">Max File Size for Uploads (MB)</Label>
                <Input type="number" id="maxFileSize" name="maxFileSize" value={settings.maxFileSize} onChange={handleChange} min="1" />
                <HelperText>Maximum allowed file size for document uploads.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="defaultApplicationStatus">Default Application Status</Label>
                <Select id="defaultApplicationStatus" name="defaultApplicationStatus" value={settings.defaultApplicationStatus} onChange={handleChange}>
                  <option value="pending">Pending</option>
                  <option value="under_review">Under Review</option>
                  {/* <option value="approved">Approved</option> */}
                </Select>
                <HelperText>The initial status assigned to newly created applications.</HelperText>
              </FormGroup>
              <SectionDivider />
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Enable Maintenance Mode</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="maintenanceMode" checked={settings.maintenanceMode} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>When enabled, users will see a full "Under Maintenance" page and cannot access the application.</HelperText>
              </FormGroup>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Close Accepting New Applications</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="applicationsClosed" checked={settings.applicationsClosed} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>When enabled, the landing page stays visible behind a grey "Applications Closed" overlay, and login/OTP is blocked server-side. Auto-enables when the Application Deadline below passes.</HelperText>
              </FormGroup>
              <FormGroup>
                <Label htmlFor="applicationDeadline">Application Deadline (Auto-closes New Applications)</Label>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Input
                    type="datetime-local"
                    id="applicationDeadline"
                    name="applicationDeadline"
                    value={settings.applicationDeadline}
                    onChange={handleChange}
                    style={{ flex: 1 }}
                  />
                  {settings.applicationDeadline && (
                    <button
                      type="button"
                      onClick={() => setSettings(prev => ({ ...prev, applicationDeadline: '' }))}
                      style={{
                        padding: '8px 14px', borderRadius: '8px', border: '1px solid #fca5a5',
                        background: '#fef2f2', color: '#dc2626', fontSize: '0.85rem', fontWeight: 600,
                        cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s'
                      }}
                    >
                      Clear
                    </button>
                  )}
                </div>
                <HelperText>
                  When set, the system will automatically enable maintenance mode once this date/time passes.
                  A countdown timer will appear on the login page. Users already filling forms will not be interrupted.
                  {settings.applicationDeadline && (
                    <span style={{ display: 'block', marginTop: '4px', color: new Date(settings.applicationDeadline) <= new Date() ? '#dc2626' : '#059669', fontWeight: 600 }}>
                      {new Date(settings.applicationDeadline) <= new Date()
                        ? `Deadline has passed (${new Date(settings.applicationDeadline).toLocaleString()})`
                        : `Deadline: ${new Date(settings.applicationDeadline).toLocaleString()}`
                      }
                    </span>
                  )}
                </HelperText>
              </FormGroup>
              <SectionDivider />
              <SectionTitle style={{ fontSize: '1.2rem', borderBottom: 'none', paddingBottom: '0.5rem' }}>Document Request Feature</SectionTitle>
              <FormGroup>
                <ToggleRow>
                  <ToggleLabel>Enable Document Request via Email</ToggleLabel>
                  <ToggleSwitchContainer>
                    <input type="checkbox" name="documentRequestFeatureEnabled" checked={settings.documentRequestFeatureEnabled} onChange={handleChange} />
                    <ToggleSlider />
                  </ToggleSwitchContainer>
                </ToggleRow>
                <HelperText>Allow admins to request documents from applicants via email. Applicants receive a secure link to upload documents.</HelperText>
              </FormGroup>
              <div style={{ display: 'flex', gap: '1rem', alignItems: 'center', marginTop: '0.5rem', marginBottom: '1rem' }}>
                <Button type="button" onClick={handleTestDocumentRequest} disabled={testingDocRequest || !settings.documentRequestFeatureEnabled}>
                  {testingDocRequest ? 'Testing...' : 'Test Document Request Feature'}
                </Button>
              </div>
              {docRequestTestResult && (
                <TestResult success={docRequestTestResult.success} style={{ marginTop: '0.5rem', marginBottom: '1rem' }}>{docRequestTestResult.message}</TestResult>
              )}
              <SaveButton type="submit" disabled={loading}>
                {loading ? 'Saving...' : 'Save Advanced Settings'}
              </SaveButton>
            </SettingsForm>
            
            <SectionDivider />
            
            {/* Bulk Application Purge Section */}
            <SectionTitle style={{ fontSize: '1.2rem', borderBottom: 'none', paddingBottom: '0.5rem', marginTop: '1.5rem' }}>
              🗑️ Bulk Application Purge
            </SectionTitle>
            <HelperText style={{ marginBottom: '1rem', color: '#dc2626' }}>
              <strong>Warning:</strong> This feature permanently deletes applications and their related documents. Use with extreme caution.
            </HelperText>
            
            <FormGroup>
              <Label>Purge Type</Label>
              <Select 
                value={purgeType} 
                onChange={(e) => {
                  setPurgeType(e.target.value as 'all' | 'dateRange' | 'byApplicationId');
                  setPurgePreview(null);
                  setPurgeResult(null);
                  setPurgeApplicationId('');
                }}
              >
                <option value="dateRange">By Date Range</option>
                <option value="byApplicationId">By Application ID</option>
                <option value="all">All Applications</option>
              </Select>
            </FormGroup>
            
            {purgeType === 'byApplicationId' && (
              <FormGroup>
                <Label>Application ID</Label>
                <Input 
                  type="text" 
                  value={purgeApplicationId} 
                  onChange={(e) => {
                    setPurgeApplicationId(e.target.value);
                    setPurgePreview(null);
                  }}
                  placeholder="Enter Application ID (e.g., CNG-18012026214951-720J)"
                />
                <HelperText>Enter the exact Application ID to purge a single application.</HelperText>
              </FormGroup>
            )}
            
            {purgeType === 'dateRange' && (
              <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem' }}>
                <FormGroup style={{ flex: 1 }}>
                  <Label>Start Date</Label>
                  <Input 
                    type="date" 
                    value={purgeStartDate} 
                    onChange={(e) => {
                      setPurgeStartDate(e.target.value);
                      setPurgePreview(null);
                    }}
                  />
                </FormGroup>
                <FormGroup style={{ flex: 1 }}>
                  <Label>End Date</Label>
                  <Input 
                    type="date" 
                    value={purgeEndDate} 
                    onChange={(e) => {
                      setPurgeEndDate(e.target.value);
                      setPurgePreview(null);
                    }}
                  />
                </FormGroup>
              </div>
            )}
            
            <FormGroup>
              <ToggleRow>
                <ToggleLabel>Create backup before purging (recommended)</ToggleLabel>
                <ToggleSwitchContainer>
                  <input 
                    type="checkbox" 
                    checked={createBackup} 
                    onChange={(e) => setCreateBackup(e.target.checked)} 
                  />
                  <ToggleSlider />
                </ToggleSwitchContainer>
              </ToggleRow>
              <HelperText>Creates a JSON backup file that can be used to restore deleted applications.</HelperText>
            </FormGroup>
            
            <div style={{ display: 'flex', gap: '1rem', marginTop: '1rem' }}>
              <Button 
                type="button" 
                onClick={handlePurgePreview} 
                disabled={isLoadingPreview || (purgeType === 'dateRange' && (!purgeStartDate || !purgeEndDate)) || (purgeType === 'byApplicationId' && !purgeApplicationId.trim())}
              >
                {isLoadingPreview ? 'Loading...' : purgeType === 'byApplicationId' ? '🔍 Preview Application' : '🔍 Preview Applications'}
              </Button>
              
              {purgePreview && purgePreview.count > 0 && (
                <Button 
                  type="button" 
                  onClick={handlePurgeApplications} 
                  disabled={isPurging}
                  style={{ backgroundColor: '#dc2626', color: 'white' }}
                >
                  {isPurging ? 'Purging...' : `🗑️ Purge ${purgePreview.count} Applications`}
                </Button>
              )}
            </div>
            
            {purgeResult && (
              <TestResult success={purgeResult.success} style={{ marginTop: '1rem' }}>
                {purgeResult.message}
              </TestResult>
            )}
            
            {purgePreview && (
              <div style={{ marginTop: '1rem', padding: '1rem', backgroundColor: '#fef3c7', border: '1px solid #f59e0b', borderRadius: '8px' }}>
                <p style={{ margin: 0, fontWeight: 600, color: '#92400e' }}>
                  Found {purgePreview.count} application(s) matching criteria
                </p>
                {purgePreview.applications.length > 0 && (
                  <div style={{ marginTop: '0.75rem', maxHeight: '200px', overflow: 'auto' }}>
                    <table style={{ width: '100%', fontSize: '0.85rem', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ borderBottom: '1px solid #d97706' }}>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: '#92400e' }}>Application ID</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: '#92400e' }}>Email</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: '#92400e' }}>Created</th>
                          <th style={{ textAlign: 'left', padding: '0.5rem', color: '#92400e' }}>Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {purgePreview.applications.map((app: any) => (
                          <tr key={app.applicationId} style={{ borderBottom: '1px solid #fcd34d' }}>
                            <td style={{ padding: '0.5rem', color: '#78350f' }}>{app.applicationId}</td>
                            <td style={{ padding: '0.5rem', color: '#78350f' }}>{app.email}</td>
                            <td style={{ padding: '0.5rem', color: '#78350f' }}>{new Date(app.createdAt).toLocaleDateString()}</td>
                            <td style={{ padding: '0.5rem', color: '#78350f' }}>{app.status || 'pending'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {purgePreview.count > 100 && (
                      <p style={{ margin: '0.5rem 0 0', fontSize: '0.8rem', color: '#92400e' }}>
                        Showing first 100 of {purgePreview.count} applications
                      </p>
                    )}
                  </div>
                )}
              </div>
            )}
            
            {/* Purge Backups Section */}
            <SectionDivider />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <SectionTitle style={{ fontSize: '1.1rem', borderBottom: 'none', paddingBottom: 0, margin: 0 }}>
                📦 Purge Backups
              </SectionTitle>
              <Button type="button" onClick={fetchPurgeBackups} disabled={isLoadingBackups}>
                {isLoadingBackups ? 'Loading...' : '🔄 Refresh'}
              </Button>
            </div>
            
            {purgeBackups.length === 0 ? (
              <p style={{ color: '#6b7280', fontStyle: 'italic' }}>No purge backups available.</p>
            ) : (
              <div style={{ maxHeight: '300px', overflow: 'auto' }}>
                {purgeBackups.map((backup) => (
                  <div 
                    key={backup.filename} 
                    style={{ 
                      padding: '0.75rem', 
                      marginBottom: '0.5rem', 
                      backgroundColor: '#f3f4f6', 
                      borderRadius: '8px',
                      border: '1px solid #e5e7eb'
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <p style={{ margin: 0, fontWeight: 600, fontSize: '0.9rem' }}>
                          {backup.applicationsCount} application(s)
                        </p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.8rem', color: '#6b7280' }}>
                          {backup.purgeType === 'all' ? 'All applications' : `Date range: ${backup.dateRange?.startDate} to ${backup.dateRange?.endDate}`}
                        </p>
                        <p style={{ margin: '0.25rem 0 0', fontSize: '0.75rem', color: '#9ca3af' }}>
                          Created: {new Date(backup.backupDate || backup.createdAt).toLocaleString()} • Size: {formatFileSize(backup.size)}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        <Button 
                          type="button" 
                          onClick={() => handleRestoreBackup(backup.filename)}
                          disabled={isRestoring}
                          style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem' }}
                        >
                          ↩️ Restore
                        </Button>
                        <Button 
                          type="button" 
                          onClick={() => handleDeleteBackup(backup.filename)}
                          style={{ padding: '0.4rem 0.8rem', fontSize: '0.8rem', backgroundColor: '#fee2e2', color: '#991b1b' }}
                        >
                          🗑️
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            <SectionDivider />
            <div style={{ backgroundColor: '#e0f2fe', border: '1px solid #0ea5e9', borderRadius: '8px', padding: '1rem', marginTop: '1.5rem' }}>
              <p style={{ color: '#0369a1', margin: 0, fontSize: '0.9rem' }}>
                <strong>📝 Developer Note:</strong> The page files for Pages 5-13 still exist in the codebase but are no longer routed or accessible. They can be deleted later if desired.
              </p>
            </div>
          </SettingsCard>
        )}
      </TabContent>

      {/* Global save button or per-section save can be decided. For now, using per-section. */}
      {/* <SuccessMessage visible={showSuccess}>Settings saved successfully!</SuccessMessage> */}
      <SettingsCard>
        <SectionTitle>User Management</SectionTitle>
        <p>This section would allow administrators to manage admin users, roles, and permissions.</p>
        {/* This card is not yet part of the tabbed interface and can be integrated later */}
      </SettingsCard>

      {/* Modal for viewing appendix template PDFs */}
      {viewModalOpen && (
        <div 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
          onClick={() => setViewModalOpen(false)}
        >
          <div 
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '1rem',
              width: '90%',
              maxWidth: '900px',
              height: '85vh',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
              <h3 style={{ margin: 0, color: '#111827' }}>Template Preview</h3>
              <button
                onClick={() => setViewModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0.25rem'
                }}
              >
                ✕
              </button>
            </div>
            <iframe
              src={viewModalUrl}
              style={{
                flex: 1,
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                width: '100%'
              }}
              title="Template Preview"
            />
          </div>
        </div>
      )}
    </SettingsContainer>
  );
};

export default Settings;
